import { useEffect, useState } from 'react'
import {
  Alert, Button, Card, Checkbox, Form, Input, Label, Select, Stack, Table, Td, Text, TextLink, Th, VisuallyHidden,
} from '@ui'
import { getJSON, patchJSON, postJSON } from '../api.js'
import { formatDate, formatTime } from '../format.js'

// "Share…" on the walk page (2026-10-11; plan.md "Public walk links"), and "Share these photos…" on
// the Photos page (2026-10-25: a range of days; SharePanel is both): make a link anyone can open
// (/s/<token>, read-only) for a day, a week, a month or until stopped, list this walk's links
// with their views, copy one again, or stop it. What a link shows is decided on the server
// (cmd/server/shares.go); "Hide personal details" and photos are on by default, videos off (their
// sound is shared as recorded; their recording time and phone details are blanked). Each link can
// carry a note (2026-10-25: who it went to, or why), for the owner only: the shared page never
// gets it. The note can be changed later on any link, working or ended (ShareNote).

const EXPIRY = [
  { value: 'day', label: '1 day' },
  { value: 'week', label: '1 week' },
  { value: 'month', label: '1 month' },
  { value: 'never', label: 'Until I stop it' },
]

export const when = (iso) => `${formatDate(iso)}, ${formatTime(iso)}`
export const linkOf = (share) => `${window.location.origin}/s/${share.token}`

export function mediaText(share) {
  const media = [share.show_photos && 'Photos', share.show_videos && 'videos'].filter(Boolean)
  if (!media.length) return 'No photos or videos'
  return media.length === 2 ? 'Photos and videos' : media[0][0].toUpperCase() + media[0].slice(1)
}

export function stateText(share) {
  if (share.state === 'stopped') return `Stopped ${when(share.stopped_at)}`
  if (share.state === 'expired') return `Expired ${when(share.expires_at)}`
  return share.expires_at ? `Until ${when(share.expires_at)}` : 'Until stopped'
}

export const NOTE_MAX = 200

// A link to the Photos page's days, as the server's dayRangeLabel: "Mon 12 Oct 2026",
// "October 2026", "2026", "1 Jan – 15 Mar 2026", "20 Dec 2025 – 3 Jan 2026". from/to: YYYY-MM-DD.
export function dayRange(from, to) {
  const d = (iso) => new Date(`${iso}T12:00:00Z`)
  const f = (iso, opts) => d(iso).toLocaleDateString('en-GB', { timeZone: 'UTC', ...opts })
  const [fy, fm, fd] = from.split('-').map(Number)
  const [ty, tm, td] = to.split('-').map(Number)
  const lastOfMonth = new Date(Date.UTC(ty, tm, 0)).getUTCDate()
  if (from === to) return f(from, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).replace(',', '')
  if (fy === ty && fm === 1 && fd === 1 && tm === 12 && td === 31) return String(fy)
  if (fy === ty && fm === tm && fd === 1 && td === lastOfMonth) return f(from, { month: 'long', year: 'numeric' })
  const short = { day: 'numeric', month: 'short' }
  const long = { ...short, year: 'numeric' }
  return `${f(from, fy === ty ? short : long)} – ${f(to, long)}`
}

// The marker in the walks table for a walk with a working link, its notes in the tooltip.
export function ShareIcon({ notes, ...style }) {
  const label = notes ? `Shared: ${notes}` : 'Shared'
  return (
    <Text className="photo-marker" muted title={label} {...style}>
      <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true">
        <path d="M12.5 3.5 3.5 8l9 4.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
        <g fill="currentColor"><circle cx="12.5" cy="3.5" r="2.2" /><circle cx="3.5" cy="8" r="2.2" /><circle cx="12.5" cy="12.5" r="2.2" /></g>
      </svg>
      <VisuallyHidden>{label}</VisuallyHidden>
    </Text>
  )
}

// A link's note in the Share panel and the home page's Shared links: the text with "Edit" (or "Add
// note"), which turns into a box; Enter or Save keeps it (PATCH /api/shares/:id), Escape or Cancel
// drops the change. onSaved gets the link as the server returns it.
export function ShareNote({ share, onSaved, muted = false }) {
  const [draft, setDraft] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      onSaved(await patchJSON(`/api/shares/${share.id}`, { note: draft }))
      setDraft(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }
  if (draft == null) {
    return (
      <Stack align="center" gap={2}>
        {share.note && <Text muted={muted}>{share.note}</Text>}
        <Button size="sm" variant="link" p={0} onClick={() => setDraft(share.note ?? '')}>{share.note ? 'Edit' : 'Add note'}</Button>
      </Stack>
    )
  }
  return (
    <Form
      display="flex" align="center" gap={2}
      onSubmit={(e) => { e.preventDefault(); save() }}
      onKeyDown={(e) => { if (e.key === 'Escape') { setDraft(null); setError(null) } }}
    >
      <Input
        size="sm" maxLength={NOTE_MAX} aria-label="Note, just for you" autoFocus
        value={draft} onChange={(e) => setDraft(e.target.value)} style={{ minWidth: '10rem' }}
      />
      <Button size="sm" type="submit" disabled={busy}>Save</Button>
      <Button size="sm" variant="link" p={0} onClick={() => { setDraft(null); setError(null) }}>Cancel</Button>
      {error && <Text text="small" tone="danger">{error}</Text>}
    </Form>
  )
}

// The share panel: a button showing how many links work, which opens the form and this thing's
// links. listPath/createPath: the API for them; extra: more of the create body (a photo link's
// days); fields: more form controls; intro: the explanation; columns: [{label, cell(share)}] before
// the usual ones; aside: something for the right of the button's row (the Photos page's place
// list), kept above the panel while it's open.
export function SharePanel({
  title, button, intro, listPath, createPath, extra = {}, fields = null, columns = [], ready = true, aside = null,
}) {
  const [open, setOpen] = useState(false)
  const [shares, setShares] = useState(null)
  const [expires, setExpires] = useState('week')
  const [hide, setHide] = useState(true)
  const [showPhotos, setShowPhotos] = useState(true)
  const [showVideos, setShowVideos] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(null)

  useEffect(() => {
    getJSON(listPath).then(setShares).catch(() => setShares([]))
  }, [listPath])

  const run = async (action) => {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }
  const create = () => run(async () => {
    const share = await postJSON(createPath, { expires, hide_details: hide, show_photos: showPhotos, show_videos: showVideos, note, ...extra })
    setShares((list) => [share, ...(list ?? [])])
    setNote('')
    copy(share)
  })
  const stop = (share) => run(async () => {
    const stopped = await postJSON(`/api/shares/${share.id}/stop`)
    setShares((list) => list.map((s) => (s.id === stopped.id ? stopped : s)))
  })
  const copy = (share) => {
    navigator.clipboard?.writeText(linkOf(share)).then(() => setCopied(share.id)).catch(() => {})
  }

  const active = (shares ?? []).filter((s) => s.state === 'active').length
  if (!open) {
    return (
      <Stack wrap align="center" justify="between" gap={2} mb={3}>
        <Button size="sm" variant="secondary" outline onClick={() => setOpen(true)}>
          {button}{active > 0 && ` (${active} active ${active === 1 ? 'link' : 'links'})`}
        </Button>
        {aside}
      </Stack>
    )
  }
  return (
    <>
      {aside && <Stack justify="end" mb={2}>{aside}</Stack>}
      <Card title={title} mb={3}>
        <Text as="p" text="small" muted>{intro}</Text>
        <Form display="flex" wrap align="center" gap={2} mb={3} onSubmit={(e) => { e.preventDefault(); create() }}>
          {fields}
          <Label htmlFor="share-expires" mb={0} text="small">Link works for</Label>
          <Select id="share-expires" size="sm" autoWidth value={expires} onChange={(e) => setExpires(e.target.value)}>
            {EXPIRY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          <Checkbox toggle id="share-hide" text="small" ms={2} label="Hide personal details" checked={hide} onChange={(e) => setHide(e.target.checked)} />
          <Checkbox toggle id="share-photos" text="small" label="Photos" checked={showPhotos} onChange={(e) => setShowPhotos(e.target.checked)} />
          <Checkbox toggle id="share-videos" text="small" label="Videos" checked={showVideos} onChange={(e) => setShowVideos(e.target.checked)} />
          <Input
            id="share-note" size="sm" maxLength={NOTE_MAX} placeholder="Note, just for you (who it's for…)" aria-label="Note, just for you"
            value={note} onChange={(e) => setNote(e.target.value)} style={{ maxWidth: '18rem' }}
          />
          <Button size="sm" type="submit" disabled={busy || !ready}>Make a link</Button>
          <Button size="sm" variant="link" onClick={() => setOpen(false)}>Close</Button>
        </Form>
        {error && <Alert variant="danger" compact>{error}</Alert>}
        {shares?.length > 0 && (
          <Table responsive middle mb={0}>
            <thead>
              <tr>
                <th>Link</th>{columns.map((c) => <th key={c.label}>{c.label}</th>)}
                <th>Works</th><th>Details</th><th>Shows</th><th>Note</th><Th numeric>Views</Th><th />
              </tr>
            </thead>
            <tbody>
              {shares.map((s) => (
                <tr key={s.id}>
                  <Td muted={s.state !== 'active'}>
                    <Stack align="center" gap={2}>
                      <TextLink href={linkOf(s)} target="_blank" rel="noreferrer" truncate style={{ maxWidth: '14rem' }}>
                        /s/{s.token}
                      </TextLink>
                      {s.state === 'active' && (
                        <Button size="sm" variant="link" p={0} onClick={() => copy(s)}>{copied === s.id ? 'Copied' : 'Copy'}</Button>
                      )}
                    </Stack>
                  </Td>
                  {columns.map((c) => <Td key={c.label} nowrap muted={s.state !== 'active'}>{c.cell(s)}</Td>)}
                  <Td nowrap muted={s.state !== 'active'}>{stateText(s)}</Td>
                  <Td muted={s.state !== 'active'}>{s.hide_details ? 'Hidden' : 'Shown'}</Td>
                  <Td nowrap muted={s.state !== 'active'}>{mediaText(s)}</Td>
                  <td>
                    <ShareNote share={s} muted={s.state !== 'active'} onSaved={(saved) => setShares((list) => list.map((x) => (x.id === saved.id ? saved : x)))} />
                  </td>
                  <Td numeric muted={s.state !== 'active'} title={s.last_viewed_at ? `Last opened ${when(s.last_viewed_at)}` : undefined}>
                    {s.view_count}
                  </Td>
                  <Td textAlign="end">
                    {s.state === 'active' && (
                      <Button size="sm" variant="danger" outline disabled={busy} onClick={() => stop(s)}>Stop</Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  )
}

// The walk page's "Share…".
export default function ShareControl({ walkId }) {
  return (
    <SharePanel
      title="Share this walk" button="Share…"
      listPath={`/api/walks/${walkId}/shares`} createPath={`/api/walks/${walkId}/shares`}
      intro={<>
        Anyone with the link can see this walk, read-only, until it expires or you stop it.
        Hiding personal details leaves out the first and last 300 m of the route, the notes, the
        times of day, the phone, and photos and videos taken in the hidden parts. Photos are shared
        as resized copies without their camera details. Videos are shared without their recording
        time or phone details, but with their sound as recorded.
      </>}
    />
  )
}

// The days a link to the Photos page covers, for the table: the share's own range.
const daysOf = (s) => (s.date_from && s.date_to ? dayRange(s.date_from.slice(0, 10), s.date_to.slice(0, 10)) : '')

// The Photos page's "Share these photos…": the photos of the walks that started from `from` to
// `to` (UK days, YYYY-MM-DD; the page's year filter sets where they start).
export function SharePhotos({ initialFrom, initialTo, aside = null }) {
  const [from, setFrom] = useState(initialFrom)
  const [to, setTo] = useState(initialTo)
  useEffect(() => { setFrom(initialFrom); setTo(initialTo) }, [initialFrom, initialTo])
  const ready = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to
  return (
    <SharePanel
      title="Share these photos" button="Share these photos…"
      listPath="/api/photos/shares" createPath="/api/photos/shares" extra={{ from, to }} ready={ready} aside={aside}
      columns={[{ label: 'Days', cell: daysOf }]}
      fields={<>
        <Label htmlFor="share-from" mb={0} text="small">Walks from</Label>
        <Input id="share-from" type="date" size="sm" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 'auto' }} />
        <Label htmlFor="share-to" mb={0} text="small">to</Label>
        <Input id="share-to" type="date" size="sm" value={to} onChange={(e) => setTo(e.target.value)} style={{ width: 'auto' }} />
      </>}
      intro={<>
        Anyone with the link can see the photos from the walks you&apos;ve chosen, by date and on a
        map, read-only, until it expires or you stop it. Photos added to those walks later show too.
        Hiding personal details leaves out photos and videos taken within 300 m of their
        walk&apos;s start or finish (and any that can&apos;t be placed on a map to check), and the
        times of day. Photos are shared as resized copies without their camera details; videos
        without their recording time or phone details, but with their sound as recorded.
      </>}
    />
  )
}
